'use client';

import { useAuthStore } from '@/app/store/use-auth-store';
import { useBranchStore } from '@/app/store/use-branch-store';
import {
  PointStatus,
  useAddressStore,
} from '@/entities/address/model/use-address-store';
import { useBrand } from '@/entities/brand/api/query';
import { useCartStore } from '@/entities/cart/model/use-cart-store';
import { useCartProducts } from '@/entities/product/api/query';
import useAutoAddingProduct from '@/entities/product/model/use-auto-adding-product';
import { useRouter } from '@/i18n/navigation';
import CardRow from '@/shared/ui/card-row';
import {
  Button,
  Center,
  Spinner,
  Stack,
  StackSeparator,
  Text,
} from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';

export default function CartList() {
  const router = useRouter();
  const t = useTranslations();

  const { user } = useAuthStore();
  const { cart } = useCartStore();
  const { branch, setBranch, setIsBranchNull } = useBranchStore();
  const { points } = useAddressStore();

  const activeAddress = points?.find(
    (item) => item.status === PointStatus.ACTIVE
  );

  const { data: brandData, isLoading: isLoadingBrand } = useBrand(
    cart?.[0]?.brand_id,
    {
      lat: activeAddress?.location?.[0],
      long: activeAddress?.location?.[1],
    }
  );

  useEffect(() => {
    if (!brandData) return;

    if (brandData.branch?.id === branch?.id) return;

    if (brandData.branch) {
      setBranch(brandData.branch);
    } else {
      setIsBranchNull(true);
    }
  }, [brandData]);
  const { productIds } = useAutoAddingProduct({
    enabled: !!cart?.[0]?.brand_id && !isLoadingBrand,
  });

  const { data, isLoading } = useCartProducts(
    {
      page: 1,
      limit: 50,
      fields: 'modifiers,categories,weight,measurement',
      client_id: user?.id,
      menu_id: branch?.menu_id,
      product_ids: productIds,
      partner_shipper_id: cart?.[0]?.shipper_id,
    },
    !!cart?.[0]?.shipper_id && !isLoadingBrand
  );

  if (!cart?.length) {
    return (
      <>
        <Center
          flexDir={'column'}
          alignItems="center"
          justifyContent="center"
          h="80vh"
        >
          <Text fontSize="lg" fontWeight="bold" textAlign="center">
            {t('your_cart_empty')}
          </Text>
          <Text fontSize={'sm'}>
            {t('the_items_you_order_will_appear_here')}
          </Text>
          <Button
            onClick={() => router.push('/')}
            bg="primary"
            color="primary.text"
            _hover={{
              bg: 'primary.hover',
            }}
            rounded={12}
            size={'xl'}
            mt={4}
          >
            {t('back_to_menu')}
          </Button>
        </Center>
      </>
    );
  }

  if (isLoadingBrand || isLoading) {
    return (
      <Stack
        mt={4}
        separator={<StackSeparator borderColor={'background'} />}
        gap={3}
      >
        <Center p={4}>
          <Spinner size="lg" color="primary" />
        </Center>
      </Stack>
    );
  }

  return (
    <Stack
      mt={4}
      separator={<StackSeparator borderColor={'background'} />}
      gap={3}
    >
      {data?.objects?.[0]?.products?.map((item) => {
        const cartItem = cart.find((el) => item.id === el.product_id);
        if (!cartItem) {
          return null;
        }

        return <CardRow key={item.id} cartItem={cartItem} data={item} />;
      })}
    </Stack>
  );
}
